import React, { useMemo, useState } from 'react'
import { createContext } from 'lib/createContext'
import { TierEligibility, TierType } from 'pages/Shifts/ShiftDetail/types'
import { useTierEligibilitiesByShiftIdQuery } from 'queries/shifts/useTierEligibilitiesByShiftIdQuery'
import { useShiftDetailContextValue } from '../../../context/ShiftDetailProvider'
import { useListingsOverviewContextValue } from './ListingsOverviewProvider'
import { parseErrorMessage } from 'api'
import { Box } from 'lucide-react'
import { SectionHeading } from '../components/SectionHeading'
import { Message } from '@workwhile/ui'

type TierStatsContextState = {
  state: {
    listingId: number
    eligibilities: TierEligibility[]
    dataUpdatedAt: number
    isFetching: boolean
    isLoading: boolean
    isError: boolean
    error: string
    isAnyTierEligibleForExploration: boolean
    selectedTier: TierType | undefined
  }
  actions: {
    refetch: () => void
    setSelectedTier: (tier: TierType | undefined) => void
  }
}

const [Context, useTierStatsContextValue] =
  createContext<TierStatsContextState>({
    name: 'TierStatsContext',
  })

export const TierStatsProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const {
    state: { id: shiftId },
  } = useShiftDetailContextValue()

  const {
    state: { listingId: overviewListingId },
  } = useListingsOverviewContextValue()

  const {
    data,
    isLoading,
    isError,
    error,
    dataUpdatedAt,
    refetch,
    isFetching,
  } = useTierEligibilitiesByShiftIdQuery(shiftId)

  const [selectedTier, setSelectedTier] = useState<TierType | undefined>(
    undefined
  )

  const eligibilities = data?.eligibilities ?? []

  const isAnyTierEligibleForExploration = useMemo(() => {
    return eligibilities.some(
      (eligibility) =>
        eligibility.activeStatus === 'past' ||
        eligibility.activeStatus === 'active'
    )
  }, [eligibilities])

  const listingId = data?.listingId ?? overviewListingId

  if (!listingId) {
    return (
      <Box>
        <SectionHeading />
        <Message variant="error" borderRadius="small">
          This shift does not have any listings. Listing overview is
          unavailable.
        </Message>
      </Box>
    )
  }

  const value = {
    state: {
      listingId: listingId,
      eligibilities,
      dataUpdatedAt,
      isFetching,
      isLoading,
      isError,
      error: parseErrorMessage(error),
      isAnyTierEligibleForExploration,
      selectedTier,
    },
    actions: { refetch, setSelectedTier },
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export { useTierStatsContextValue }
